import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/contexts/AuthContext'
import api from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Building2, Users, FileText, DollarSign, TrendingUp, ArrowRight, CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

const OrgDashboard: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const { currentOrg, exitOrgView } = useOrganization()
  const { selectClinic } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [openingClinicId, setOpeningClinicId] = React.useState<string | null>(null)

  // Fetch overview metrics
  const { data: overview, isLoading: isOverviewLoading } = useQuery({
    queryKey: ['org-dashboard-overview', orgId],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/dashboard/overview`)
      return res.data?.data
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minute cache
  })

  const { data: billingOverview } = useQuery({
    queryKey: ['org-billing-overview', orgId],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/billing/overview`)
      return res.data?.data
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch clinic performance
  const { data: performance, isLoading: isPerfLoading } = useQuery({
    queryKey: ['org-dashboard-performance', orgId],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/dashboard/clinics-performance?page_size=10&sort_by=revenue&sort_dir=desc`)
      return res.data?.data
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  })

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
  }

  const handleOpenClinic = async (clinicId: string) => {
    try {
      setOpeningClinicId(clinicId)
      await selectClinic(clinicId)
      localStorage.setItem('selectedClinicId', clinicId)
      localStorage.setItem('selectedClinicTimestamp', Date.now().toString())
      exitOrgView()
      navigate('/', { replace: true })
    } catch (error) {
      toast({
        title: 'Unable to open clinic',
        description: 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setOpeningClinicId(null)
    }
  }

  const kpiCards = [
    {
      title: 'Total Clinics',
      value: overview?.total_clinics ?? 0,
      icon: Building2,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      title: 'Total Patients',
      value: overview?.total_patients?.toLocaleString() ?? '0',
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      title: 'Total Orders',
      value: overview?.total_prescriptions?.toLocaleString() ?? '0',
      icon: FileText,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      title: 'Total Revenue',
      value: overview ? formatCurrency(overview.total_revenue_cents) : '$0',
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      title: 'Unbilled Fees',
      value: billingOverview
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(billingOverview.totalUnbilled)
        : '$0',
      icon: CreditCard,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {currentOrg?.name || 'Organization'} Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Overview of all clinics in your organization
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpiCards.map((card) => (
          <Card key={card.title}>
            <CardContent className="pt-6">
              {isOverviewLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{card.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{card.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${card.bg}`}>
                    <card.icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Averages */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Avg Patients per Clinic</p>
                  <p className="text-xl font-bold text-slate-900">{overview.avg_patients_per_clinic}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-50">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Avg Revenue per Clinic</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(overview.avg_revenue_per_clinic_cents)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Clinic Performance Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Top Clinics by Revenue</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/org/${orgId}/clinics`)}>
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {isPerfLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : performance?.clinics?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-3 font-medium">Clinic</th>
                    <th className="pb-3 font-medium">Location</th>
                    <th className="pb-3 font-medium text-right">Patients</th>
                    <th className="pb-3 font-medium text-right">Orders</th>
                    <th className="pb-3 font-medium text-right">Revenue</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {performance.clinics.map((clinic: Record<string, unknown>) => (
                    <tr key={clinic.id as string} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-3 font-medium text-slate-900">{clinic.name as string}</td>
                      <td className="py-3 text-slate-500">
                        {clinic.city as string}{clinic.state ? `, ${clinic.state}` : ''}
                      </td>
                      <td className="py-3 text-right">{(clinic.patient_count as number)?.toLocaleString()}</td>
                      <td className="py-3 text-right">{(clinic.prescription_count as number)?.toLocaleString()}</td>
                      <td className="py-3 text-right font-medium">{formatCurrency(clinic.revenue_cents as number)}</td>
                      <td className="py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenClinic(clinic.id as string)}
                          disabled={openingClinicId === (clinic.id as string)}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-8 text-center">No clinic data available yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default OrgDashboard
